/*
 * lazy-preload.js -- preloaduje obrazky karet/dlazdic PREDEM (mimo
 * viewport), aby se predeslo tomu, ze se lazy obrazek nacte PRAVE V
 * OKNE TAPU (1.27.13, zaverecna oprava dvojiteho tapu na mobilu po
 * scrollu). Koordinator zmeril, ze pri navratu listy adresy (iOS Chrome,
 * po prvnim tapu po scrollu) se za 124-168 ms nacte lazy obrazek pod
 * puvodnim spodnim okrajem -- novy obsah presne v okamziku, kdy WebKit
 * jeste vyhodnocuje hover fazi tapu, a klik zahodi.
 *
 * PROC NE "vsechny obrazky eager" (zvazena a zamitnuta varianta A):
 * zmereno na stagingu -- vypis her se vsemi obrazky eager = 14,7 MB
 * (dnes, jen lazy co se nacte pri startu = 0,7 MB), homepage 4,7 MB,
 * detail hry 5,0 MB. I jen karty samotne by byly ~9 MB. Misto toho
 * vlastni IntersectionObserver s VELKOU predstihovou zonou
 * (`ROOT_MARGIN`): obrazek se prepne na `eager` (a tim si ho prohlizec
 * sam dotahne) driv, nez se dostane do viewportu, takze v okamziku,
 * kdy uzivatel na nej doscrolluje a klepne, uz je NACTENY -- bez
 * nutnosti tahat VSECHNY obrazky stranky predem.
 *
 * CIL SELEKTORU (`CARD_IMG_SELECTOR`): jen `<img loading="lazy">`
 * UVNITR karet/dlazdic vypisu a karuselu -- `.lcard`/`.gcard` (vypisy
 * her/lokalit), `.prog` (homepage karty programu), `.ecard`
 * (eventy/reference), `.spol` (stripband lokalit), `.yt-facade`/
 * `.dvideo` (fasady videa). Blog, galerie a paticka ZUSTAVAJI lazy
 * (koordinator: tam k dvojitemu tapu po scrollu nedochazi stejnym
 * zpusobem, a plosne rozsireni by zbytecne navysilo vahu).
 *
 * SPOUSTEC JE SCROLL, NE RESIZE: `IntersectionObserver` prepocitava
 * pruniky i pri zmene vysky viewportu (iOS lista adresy po tapu, viz
 * `pulse.js`/`dock.js` docblok stejneho problemu). Aby preloader SAM
 * nevyrabel `img load` v okne tapu, zaznamy do 300 ms po poslednim
 * `resize` se ODLOZI (`guardedApply()`, stejny vzor jako
 * `guardedMutate()` v `pulse.js`) -- NE jen zahodi na urovni IO
 * callbacku (to se u pulzu ukazalo jako deravé, IO neni casove
 * svazany s `resize`), ale primo v miste, kde se DEJE skutecna mutace
 * (`img.loading = 'eager'`).
 *
 * WEBKIT OVERENI (koordinator, staging): zmena `loading` z "lazy" na
 * "eager" na jiz existujicim, mimo-viewport `<img>` SAMA O SOBE spusti
 * nacteni (request odeslan, `complete`, `naturalWidth` spravne) --
 * `decode()`/`new Image()` fallback NENI potreba, `applyEager()` ho
 * proto nema, jen no-op komentar pro pripad budouci zmeny chovani
 * motoru.
 *
 * VYPNUTO PRI `prefers-reduced-data`/`saveData`: uzivatel/operator
 * site rekl "setri data" -- preloadovani PREDEM jde presne proti tomu,
 * takze modul se v tom pripade vubec nespusti (zustane cisty nativni
 * lazy loading prohlizece).
 */

import { guard } from './resize-guard.js';

/** Jak daleko PRED viewportem (nahoru i dolu) se obrazek prepne na eager. */
/*
 * 1200, ne 400: brana `resizemut` ukazala, ze nativni lazy loading WebKitu
 * ma vetsi prah nez 400 px -- obrazek 400-1000 px pod okrajem preloader
 * jeste nepřepnul, ale po rustu viewportu (navrat listy) ho WebKit dotahl
 * sam, presne v okne tapu. Predstih musi byt VETSI nez nativni prah.
 */
const ROOT_MARGIN = '1200px 0px 1200px 0px';

/** Karty/dlazdice, jejichz obrazky se preloaduji predem (viz docblok). */
const CARD_SELECTORS = ['.lcard', '.gcard', '.prog', '.ecard', '.mode', '.spol', '.yt-facade', '.dvideo'];


/**
 * @return {boolean} Chce uzivatel/operator site setrit data ("Data Saver",
 *   `prefers-reduced-data`, nebo API `navigator.connection.saveData`)?
 */
function wantsReducedData() {
	try {
		if (window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)').matches) {
			return true;
		}
	} catch {
		// Fail-open: nepodporovany media feature se bere jako "ne".
	}
	return !!(navigator.connection && navigator.connection.saveData);
}

/**
 * Skutecne prepne obrazek na eager -- AZ MIMO ochranne okno po resize
 * (viz docblok souboru, stejny princip jako `guardedMutate()` v
 * `pulse.js`). Kdyz jsme uvnitr okna, akce se odlozi na jeho konec (a
 * pri opakovanem resize se dal posouva).
 *
 * @param {HTMLImageElement} img Obrazek k preloadu.
 * @return {void}
 */
function guardedApply(img) {
	guard(function () {
		if ('lazy' === img.loading) {
			// WebKit/Chromium spusti nacteni samotnou zmenou atributu (overeno
			// na stagingu) -- zadny decode()/new Image() fallback neni potreba.
			img.loading = 'eager';
		}
	});
}

/**
 * Inicializace modulu. Vola se z `index.js` uvnitr vlastniho try/catch.
 *
 * @return {void}
 */
export function init() {
	if (wantsReducedData()) {
		return;
	}

	if (!('IntersectionObserver' in window)) {
		// Fail-open: bez pozorovatele zustava cisty nativni lazy loading.
		return;
	}

	const selector = CARD_SELECTORS.map(function (sel) {
		return sel + ' img[loading="lazy"]';
	}).join(', ');

	const imgs = document.querySelectorAll(selector);
	if (!imgs.length) {
		return;
	}

	/*
	 * PAS LOKALIT (`.spol`, homepage) JE MARQUEE: dlazdice vjizdeji zprava,
	 * takze "v zaberu" se meni kazdou chvili bez scrollu a lazy fotka se
	 * dotahne v nahodny okamzik, i behem tapu (WebKit: novy viditelny
	 * obsah). IO s okrajem tu nepomuze (vidi jen to, co je vodorovne v
	 * zaberu). Fotky pasu se proto prepnou na eager hned po `load` stranky
	 * (10 souboru; klony sdili URL, takze zadny request navic).
	 */
	const strip = Array.prototype.slice.call(document.querySelectorAll('.spol img[loading="lazy"]'));
	if (strip.length) {
		const eagerStrip = function () {
			strip.forEach(function (img) {
				guardedApply(img);
			});
		};
		if ('complete' === document.readyState) {
			eagerStrip();
		} else {
			window.addEventListener('load', eagerStrip, { once: true });
		}
	}

	const io = new IntersectionObserver(
		function (entries) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) {
					return;
				}
				io.unobserve(entry.target);
				guardedApply(entry.target);
			});
		},
		{ rootMargin: ROOT_MARGIN, threshold: 0 }
	);

	imgs.forEach(function (img) {
		io.observe(img);
	});
}
